import { FormHelperText, Grid, RadioGroup, Stack, TextField } from "@mui/material";
import { MprcContainer, MprcFormSubHeader } from "components";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcNumberField } from "components/MprcNumberField";
import { MprcRadio } from "components/MprcRadio";
import { MprcSwitch } from "components/MprcSwitch";
import React from "react";
import { useTranslation } from "react-i18next";
import { EnrollmentExcludeOptions, EnrollmentExtraTimeOptions, useEnrollmentManagementContext } from "views/lms/enrollment/Home/EnrollmentManagementWrapper";
import { EnrollmentSelectClassroom } from "views/lms/enrollment/Home/Operation/EnrollmentSelectClassroom";
import { CancelEnrollReasonAutoComplete } from "views/lms/settings/components/CancelEnrollReasonAutoComplete";

////////////////////////////////////////////////////////////////////////////

export const EnrollOperationOptionEnrollment: React.FC = () => {

    const { t } = useTranslation("lms");
    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;

    return (
        <>
            {
                training.multipleClasses &&
                <EnrollmentSelectClassroom
                    value={ctx.classroom}
                    onChange={(value) => ctx.setClassroom(value)}
                />
            }
            <Grid container alignContent="center" alignItems="center">
                <Grid>
                    <MprcSwitch
                        label={t("Enrollment.StepOperation.ForcePrerequisitesLabel")}
                        checked={ctx.options.enrollForcePrerequisites}
                        onChange={(e) => ctx.setOptions((state) => { return { ...state, enrollForcePrerequisites: e.target.checked }; })}
                    />
                </Grid>
                <Grid>
                    <MprcSwitch
                        label={t("Enrollment.StepOperation.SendNotificationLabel")}
                        checked={ctx.options.enrollSendNotification}
                        onChange={(e) => ctx.setOptions((state) => { return { ...state, enrollSendNotification: e.target.checked }; })}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export const EnrollOperationOptionCancel: React.FC = () => {

    const { t } = useTranslation("lms");
    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;

    return (
        <>
            <MprcContainer margin={training.multipleClasses ? "dense" : "none"}>
                <FormHelperText component="div" sx={{ mt: 0 }}>
                    <ul style={{ margin: 0, paddingInlineStart: 0, listStyleType: "none" }}>
                        <li>- {t("Enrollment.StepOperation.Cancel.HeaderMessageL1")}</li>
                        {
                            training.multipleClasses &&
                            <li>- {t("Enrollment.StepOperation.Cancel.HeaderMessageL2")}</li>
                        }
                        <li>- {t("Enrollment.StepOperation.Cancel.HeaderMessageL3")}</li>
                    </ul>
                </FormHelperText>
            </MprcContainer>
            {
                training.multipleClasses &&
                <EnrollmentSelectClassroom
                    value={ctx.classroom}
                    label={t("Enrollment.StepOperation.ClassroomFilterLabel")}
                    onChange={(value) => ctx.setClassroom(value)}
                />
            }
            <Stack direction="column" minWidth={"100%"} spacing={1}>
                <CancelEnrollReasonAutoComplete
                    label={t("Enrollment.StepOperation.Cancel.CancelReasonLabel")}
                    placeholder={t("Enrollment.StepOperation.Cancel.CancelReasonPlaceholder")}
                    multiSelect={false}
                    selectedItems={ctx.options.cancelReason || []}
                    onChange={items => ctx.setOptions((state) => { return { ...state, cancelReason: items }; })}
                />
                <TextField
                    name="cancelRemarks"
                    fullWidth
                    variant="outlined"
                    placeholder={t("Enrollment.StepOperation.Cancel.RemarksPlaceholder")}
                    autoComplete="off"
                    multiline
                    rows={3}
                    slotProps={{
                        input: {
                            inputProps: { maxLength: 1024 }
                        }
                    }}
                    value={ctx.options.cancelRemarks}
                    onChange={(e) => ctx.setOptions((state) => { return { ...state, cancelRemarks: e.currentTarget.value }; })}
                />
            </Stack>
        </>
    );
};

export const EnrollOperationOptionExclude: React.FC = () => {

    const { t } = useTranslation("lms");
    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;

    ///
    const handleExcludeOptionChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        ctx.setOptions((state) => { return { ...state, excludeOption: parseInt(value) }; });
    };

    return (
        <>
            <MprcContainer margin={training.multipleClasses ? "dense" : "none"}>
                <FormHelperText component="div" sx={{ mt: 0 }}>
                    <ul style={{ margin: 0, paddingInlineStart: 0, listStyleType: "none" }}>
                        <li>- {t("Enrollment.StepOperation.Exclude.HeaderMessageL1")}</li>
                        <li>- {t("Enrollment.StepOperation.Exclude.HeaderMessageL2")}</li>
                        <li>- {t("Enrollment.StepOperation.Exclude.HeaderMessageL3")}</li>
                        <li>- {t("Enrollment.StepOperation.Exclude.HeaderMessageL4")}</li>
                    </ul>
                </FormHelperText>
            </MprcContainer>
            {
                training.multipleClasses &&
                <EnrollmentSelectClassroom
                    label={t("Enrollment.StepOperation.ClassroomFilterLabel")}
                    value={ctx.classroom}
                    onChange={(value) => ctx.setClassroom(value)}
                />
            }
            <TextField
                key="excludeReason"
                name="excludeReason"
                fullWidth
                label={t("Enrollment.StepOperation.Exclude.ExcludeReasonLabel")}
                placeholder={t("Enrollment.StepOperation.Exclude.ExcludeReasonPlaceholder")}
                autoComplete="off"
                slotProps={{
                    input: {
                        inputProps: { maxLength: 255 }
                    }
                }}
                required
                value={ctx.options.excludeReason}
                onChange={(e) => ctx.setOptions({ ...ctx.options, excludeReason: e.currentTarget.value })}
            />
            <MprcContainer>
                <Stack direction="column">
                    <RadioGroup value={ctx.options.excludeOption} onChange={handleExcludeOptionChange}>
                        <Stack direction="row" spacing={4}>
                            <MprcRadio
                                value={EnrollmentExcludeOptions.OnlyLatestEnrollments}
                                label={t("Enrollment.StepOperation.Exclude.OnlyLatestEnrollmentsLabel")}
                            />
                            <MprcRadio
                                value={EnrollmentExcludeOptions.AllEnrollments}
                                label={t("Enrollment.StepOperation.Exclude.AllEnrollmentsLabel")}
                            />
                        </Stack>
                    </RadioGroup>
                    <FormHelperText>
                        {t("Enrollment.StepOperation.Exclude.ExcludeOptionHint")}
                    </FormHelperText>
                </Stack>
            </MprcContainer>
            <MprcSwitch
                label={t("Enrollment.StepOperation.Exclude.SendNotificationLabel")}
                checked={ctx.options.excludeSendNotification}
                onChange={(e) => ctx.setOptions((state) => { return { ...state, excludeSendNotification: e.target.checked }; })}
            />
        </>
    );
};

export const EnrollOperationOptionExtraTime: React.FC = () => {

    const { t } = useTranslation("lms");
    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;

    ///
    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        ctx.setOptions((state) => { return { ...state, extraTimeOption: parseInt(value) }; });
    };

    return (
        <>
            <MprcContainer>
                <FormHelperText component="div" sx={{ mt: 0 }}>
                    <ul style={{ margin: 0, paddingInlineStart: 0, listStyleType: "none" }}>
                        <li>- {t("Enrollment.StepOperation.AllowExtraTime.HeaderMessageL1")}</li>
                        {
                            training.multipleClasses &&
                            <li>- {t("Enrollment.StepOperation.AllowExtraTime.HeaderMessageL2")}</li>
                        }
                        <li>- {t("Enrollment.StepOperation.AllowExtraTime.HeaderMessageL3")}</li>
                    </ul>
                </FormHelperText>
            </MprcContainer>

            {
                training.multipleClasses &&
                <MprcContainer>
                    <EnrollmentSelectClassroom
                        label={t("Enrollment.StepOperation.ClassroomFilterLabel")}
                        value={ctx.classroom}
                        onChange={(value) => ctx.setClassroom(value)}
                    />
                </MprcContainer>
            }

            <MprcContainer margin="none">
                <MprcFormSubHeader
                    title={t("Enrollment.StepOperation.AllowExtraTime.ExtraTimeTitle")}
                    margin="dense"
                />
                <RadioGroup value={ctx.options.extraTimeOption} onChange={handleOptionChange}>
                    <Grid container spacing={8}>
                        <Grid>
                            <Grid container spacing={1}>
                                <Grid>
                                    <MprcRadio
                                        value={EnrollmentExtraTimeOptions.Days}
                                        label={t("Enrollment.StepOperation.AllowExtraTime.ExtraDaysText")}
                                    />
                                </Grid>
                                <Grid>
                                    <MprcNumberField
                                        value={ctx.options.aditionalDays}
                                        disabled={ctx.options.extraTimeOption != EnrollmentExtraTimeOptions.Days}
                                        min={0}
                                        fullWidth={false}
                                        defaultValue={1}
                                        sx={{ width: 140 }}
                                        onChange={(value) => ctx.setOptions((state) => { return { ...state, aditionalDays: (value ?? 0) }; })}
                                        slotProps={{ input: { endAdornment: t("Enrollment.StepOperation.AllowExtraTime.DaysText") } }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid>
                            <Grid container spacing={1}>
                                <Grid>
                                    <MprcRadio
                                        value={EnrollmentExtraTimeOptions.Date}
                                        label={t("Enrollment.StepOperation.AllowExtraTime.ExtraDateText")}
                                    />
                                </Grid>
                                <Grid>
                                    <MprcDatePicker
                                        value={ctx.options.untilDate}
                                        disabled={ctx.options.extraTimeOption != EnrollmentExtraTimeOptions.Date}
                                        placeholder={t("Enrollment.StepOperation.AllowExtraTime.DateText")}
                                        onChange={(date) => ctx.setOptions((state) => { return { ...state, untilDate: date }; })}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </RadioGroup>
            </MprcContainer>
        </>
    );
};

export const EnrollOperationOptionEnrollForRecycling: React.FC = () => {

    const { t } = useTranslation("lms");
    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;

    return (
        <>
            <MprcContainer>
                <FormHelperText component="div" sx={{ mt: 0 }}>
                    <ul style={{ margin: 0, paddingInlineStart: 0, listStyleType: "none" }}>
                        <li>- {t("Enrollment.StepOperation.ReEnroll.HeaderMessageL1")}</li>
                        <li>- {t("Enrollment.StepOperation.ReEnroll.HeaderMessageL2")}</li>
                        <li>- {t("Enrollment.StepOperation.ReEnroll.HeaderMessageL3")}</li>
                    </ul>
                </FormHelperText>
            </MprcContainer>
            <MprcContainer margin={training.multipleClasses ? "dense" : "none"}>
                <MprcSwitch
                    label={t("Enrollment.StepOperation.ReEnroll.ReEnrollRevokeApprovalPrevVersionLabel")}
                    checked={ctx.options.reEnrollRevokeApprovalPrevVersion}
                    onChange={(e) => ctx.setOptions((state) => { return { ...state, reEnrollRevokeApprovalPrevVersion: e.target.checked }; })}
                    helperText={t("Enrollment.StepOperation.ReEnroll.ReEnrollRevokeApprovalPrevVersionHint")}
                />

                <MprcSwitch
                    label={t("Enrollment.StepOperation.ReEnroll.ReEnrollReuseComponentLabel")}
                    checked={ctx.options.reEnrollReuseComponent}
                    onChange={(e) => ctx.setOptions((state) => { return { ...state, reEnrollReuseComponent: e.target.checked }; })}
                    helperText={t("Enrollment.StepOperation.ReEnroll.ReEnrollReuseComponentHint")}
                />
            </MprcContainer>
            {
                training.multipleClasses &&
                <EnrollmentSelectClassroom
                    value={ctx.classroom}
                    onChange={(value) => ctx.setClassroom(value)}
                />
            }
        </>
    );
};

export const EnrollOperationOptionRevoke: React.FC = () => {

    const { t } = useTranslation("lms");
    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;

    return (
        <>
            <MprcContainer margin={training.multipleClasses ? "dense" : "none"}>
                <FormHelperText component="div" sx={{ mt: 0 }}>
                    <ul style={{ margin: 0, paddingInlineStart: 0, listStyleType: "none" }}>
                        <li>- {t("Enrollment.StepOperation.Revoke.HeaderMessageL1")}</li>
                        {
                            training.multipleClasses &&
                            <li>- {t("Enrollment.StepOperation.Revoke.HeaderMessageL2")}</li>
                        }
                        <li>- {t("Enrollment.StepOperation.Revoke.HeaderMessageL3")}</li>
                        <li>- {t("Enrollment.StepOperation.Revoke.HeaderMessageL4")}</li>
                        <li>- {t("Enrollment.StepOperation.Revoke.HeaderMessageL5")}</li>
                    </ul>
                </FormHelperText>
            </MprcContainer>
            {
                training.multipleClasses &&
                <EnrollmentSelectClassroom
                    label={t("Enrollment.StepOperation.ClassroomFilterLabel")}
                    value={ctx.classroom}
                    onChange={(value) => ctx.setClassroom(value)}
                />
            }
            <TextField
                name="revokeReason"
                fullWidth
                label={t("Enrollment.StepOperation.Revoke.ReasonLabel")}
                placeholder={t("Enrollment.StepOperation.Revoke.ReasonPlaceholder")}
                autoComplete="off"
                slotProps={{
                    input: {
                        inputProps: { maxLength: 255 }
                    }
                }}
                required
                value={ctx.options.revokeReason}
                onChange={(e) => ctx.setOptions((state) => { return { ...state, revokeReason: e.target.value }; })}
            />
        </>
    );
};

export const EnrollOperationOptionUndoRevoke: React.FC = () => {

    const { t } = useTranslation("lms");
    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;

    return (
        <>
            <MprcContainer margin={training.multipleClasses ? "dense" : "none"}>
                <FormHelperText component="div" sx={{ mt: 0 }}>
                    <ul style={{ margin: 0, paddingInlineStart: 0, listStyleType: "none" }}>
                        <li>- {t("Enrollment.StepOperation.UndoRekove.HeaderMessageL1")}</li>
                        {
                            training.multipleClasses &&
                            <li>- {t("Enrollment.StepOperation.UndoRekove.HeaderMessageL2")}</li>
                        }
                        <li>- {t("Enrollment.StepOperation.UndoRekove.HeaderMessageL3")}</li>
                    </ul>
                </FormHelperText>
            </MprcContainer>
            {
                training.multipleClasses &&
                <EnrollmentSelectClassroom
                    label={t("Enrollment.StepOperation.ClassroomFilterLabel")}
                    value={ctx.classroom}
                    onChange={(value) => ctx.setClassroom(value)}
                />
            }
            <TextField
                name="undorevokeReason"
                fullWidth
                label={t("Enrollment.StepOperation.UndoRekove.ReasonLabel")}
                placeholder={t("Enrollment.StepOperation.UndoRekove.ReasonPlaceholder")}
                autoComplete="off"
                slotProps={{
                    input: {
                        inputProps: { maxLength: 255 }
                    }
                }}
                required
                value={ctx.options.undoRevokeReason}
                onChange={(e) => ctx.setOptions((state) => { return { ...state, undoRevokeReason: e.target.value }; })}
            />
        </>
    );
};

export const EnrollOperationOptionFinalize: React.FC = () => {

    const { t } = useTranslation("lms");
    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;

    return (
        <>
            {
                training.multipleClasses &&
                <EnrollmentSelectClassroom
                    value={ctx.classroom}
                    label={t("Enrollment.StepOperation.ClassroomFilterLabel")}
                    onChange={(value) => ctx.setClassroom(value)}
                />
            }
            <FormHelperText error>
                {t("Enrollment.StepOperation.Finalize.HeaderMessage")}
            </FormHelperText>
        </>
    );
};
